import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ShoppingCart, User, Menu, X, Heart } from 'lucide-react';
import axios from 'axios';
import SearchBar from './SearchBar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [cartCount, setCartCount] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    fetchCategories();
    if (user && user !== false) fetchCartCount();
  }, [user]);

  const fetchCategories = async () => {
    try {
      const { data } = await axios.get(`${API}/categories`);
      setCategories(data);
    } catch (error) { /* ignore */ }
  };

  const fetchCartCount = async () => {
    try {
      const { data } = await axios.get(`${API}/cart`, { withCredentials: true });
      setCartCount(data.reduce((s, i) => s + i.quantity, 0));
    } catch (error) { /* ignore */ }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <nav className="bg-white border-b border-border sticky top-0 z-50">
      {/* Top bar - Promo strip */}
      <div className="bg-brand-orange text-white text-xs md:text-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-1.5 text-center font-medium">
          🚚 Livraison gratuite dès 50€ d'achat • Qualité professionnelle garantie
        </div>
      </div>

      {/* Main bar */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between gap-4 h-16">
          <Link to="/" className="flex items-center flex-shrink-0" data-testid="logo-link">
            <img
              src="https://customer-assets-lqy194kg.emergentagent.net/job_outils-depot/artifacts/9102777af8777d8a_BATIPRODEAL_logo_horizontal_fond_blanc.webp"
              alt="BATIPRODEAL"
              className="h-10 md:h-12 w-auto"
            />
          </Link>

          {/* Desktop Search */}
          <div className="hidden md:flex flex-1 max-w-2xl">
            <SearchBar />
          </div>

          {/* Right Icons */}
          <div className="flex items-center gap-3 md:gap-4 flex-shrink-0">
            {user && user !== false ? (
              <>
                <Link
                  to="/account"
                  className="hidden md:flex items-center gap-1.5 text-foreground hover:text-brand-orange text-sm"
                  data-testid="account-link"
                >
                  <User className="w-5 h-5" />
                  <span className="hidden lg:inline">Compte</span>
                </Link>
                <Link
                  to="/wishlist"
                  className="flex items-center text-foreground hover:text-brand-orange"
                  data-testid="wishlist-link"
                  aria-label="Favoris"
                >
                  <Heart className="w-5 h-5" />
                </Link>
                <Link
                  to="/cart"
                  className="relative flex items-center text-foreground hover:text-brand-orange"
                  data-testid="cart-link"
                  aria-label="Panier"
                >
                  <ShoppingCart className="w-5 h-5" />
                  {cartCount > 0 && (
                    <span className="absolute -top-2 -right-2 bg-brand-orange text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                      {cartCount}
                    </span>
                  )}
                </Link>
                {user.role === 'admin' && (
                  <Link
                    to="/admin"
                    className="hidden lg:inline-block bg-brand-dark text-white px-3 py-1.5 text-xs font-medium rounded-sm hover:bg-black"
                    data-testid="admin-link"
                  >
                    Admin
                  </Link>
                )}
              </>
            ) : (
              <Link
                to="/login"
                className="text-sm font-medium text-foreground hover:text-brand-orange"
                data-testid="login-link"
              >
                Connexion
              </Link>
            )}
            <button
              className="md:hidden text-foreground"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-button"
              aria-label="Menu"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Search */}
        <div className="md:hidden pb-3">
          <SearchBar mobile onSelect={() => setMobileMenuOpen(false)} />
        </div>
      </div>

      {/* Categories bar */}
      <div className="hidden md:block bg-surface border-t border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-5 py-2.5 overflow-x-auto">
            <Link
              to="/products"
              className="text-sm font-semibold text-foreground hover:text-brand-orange whitespace-nowrap"
              data-testid="all-products-link"
            >
              Tous les produits
            </Link>
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/products?category=${cat.id}`}
                className="text-sm font-medium text-foreground hover:text-brand-orange whitespace-nowrap"
                data-testid={`category-${cat.id}-link`}
              >
                {cat.name}
              </Link>
            ))}
            <Link
              to="/preorders"
              className="text-sm font-semibold text-brand-orange hover:underline whitespace-nowrap flex items-center gap-1"
              data-testid="preorders-nav-link"
              style={{ display: 'none' }}
            >
              <span>Précommandes</span>
            </Link>
            <div className="flex-1"></div>
            <Link to="/about" className="text-sm font-medium text-muted-foreground hover:text-brand-orange whitespace-nowrap" data-testid="about-link">
              À propos
            </Link>
            <Link to="/contact" className="text-sm font-medium text-muted-foreground hover:text-brand-orange whitespace-nowrap" data-testid="contact-link">
              Contact
            </Link>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-border">
          <div className="px-4 py-4 space-y-2">
            <Link
              to="/products"
              className="block py-2 font-medium hover:text-brand-orange"
              onClick={() => setMobileMenuOpen(false)}
            >
              Tous les produits
            </Link>
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/products?category=${cat.id}`}
                className="block py-2 font-medium hover:text-brand-orange"
                onClick={() => setMobileMenuOpen(false)}
              >
                {cat.name}
              </Link>
            ))}
            <Link
              to="/preorders"
              className="block py-2 font-semibold text-brand-orange"
              onClick={() => setMobileMenuOpen(false)}
              style={{ display: 'none' }}
            >
              🔥 Précommandes mutualisées
            </Link>
            <Link
              to="/about"
              className="block py-2 font-medium hover:text-brand-orange"
              onClick={() => setMobileMenuOpen(false)}
            >
              À propos
            </Link>
            <Link
              to="/contact"
              className="block py-2 font-medium hover:text-brand-orange"
              onClick={() => setMobileMenuOpen(false)}
            >
              Contact
            </Link>
            {user && user !== false && (
              <>
                <Link
                  to="/account"
                  className="block py-2 font-medium hover:text-brand-orange"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Mon compte
                </Link>
                {user.role === 'admin' && (
                  <Link
                    to="/admin"
                    className="block py-2 font-medium hover:text-brand-orange"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Administration
                  </Link>
                )}
                <button
                  onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                  className="block w-full text-left py-2 font-medium hover:text-brand-orange"
                  data-testid="logout-button"
                >
                  Déconnexion
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
